/**
 * Buyer's Report web view (v2).
 *
 * Mirrors the 8-section information architecture of the PDF generator —
 * see memory `project-buyer-report-v2-design`. Both the PDF and this view
 * read from the same `BuyerReport` shape and use the same hand-written
 * copy (deterministic — no genAI). When changing copy, edit
 * `components/buyer-report/copy.ts` AND
 * `services/buyer-report-pdf/copy.ts` together.
 */

import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../utils/api';
import {
  Address,
  EpcRating,
  FloodRiversAndSeaZone,
  PlanningReport,
  RiskFlag,
  SurfaceWaterRisk,
} from '@property-intel/shared';
import Card from './ui/Card';
import Button from './ui/Button';
import Skeleton from './ui/Skeleton';
import { Callout } from './buyer-report/Callout';
import {
  COVERAGE,
  EPC,
  FLOOD,
  formatGovDate,
  formatGovDateTime,
  HEADLINE,
  HERITAGE,
  HISTORY,
  PLANNING,
  TAX,
} from './buyer-report/copy';

interface BuyerReportViewerProps {
  reportId: string;
  onPaymentRequired: () => void;
  /**
   * When true, the payment CTA shows a "Redirecting to Stripe..." label and
   * sets `aria-busy` — signalling that `onPaymentRequired` is in flight.
   * Optional because callers that don't yet track the redirect state can
   * omit it; defaults to `false`.
   */
  isPaymentPending?: boolean;
  /**
   * Optional error message from a failed payment initiation. Not rendered
   * by this component today (the caller owns error display in a page-level
   * banner). Reserved for future co-location.
   */
  paymentError?: string | null;
}

interface PreviewData {
  address: Address;
  summary: string;
  riskLevel: 'low' | 'medium' | 'high';
  keyFindings: string[];
}

const RATING_BG: Record<EpcRating, string> = {
  A: 'bg-green-700',
  B: 'bg-green-600',
  C: 'bg-lime-500',
  D: 'bg-yellow-400 text-slate-950',
  E: 'bg-orange-500',
  F: 'bg-orange-700',
  G: 'bg-red-600',
  unknown: 'bg-slate-400',
};

export function BuyerReportViewer({
  reportId,
  onPaymentRequired,
  isPaymentPending = false,
}: BuyerReportViewerProps) {
  const [report, setReport] = useState<PlanningReport | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    void loadReport();
  }, [reportId]);

  const loadReport = async () => {
    try {
      const data = await api.getPlanningReport(reportId);

      if (!data.report.isPaid && data.previewData) {
        setPreviewData(data.previewData);
        // Placeholder shape for the unpaid preview state. The new BuyerReport
        // sections (planning/flood/epc/title/taxFlags) plus riskFlags +
        // unavailableSections are required by the type but never read on the
        // unpaid preview path — keyFindings come from `previewData` instead.
        setReport({
          reportId: data.report.reportId,
          transactionId: data.report.transactionId,
          address: data.report.address,
          generatedAt: data.report.generatedAt,
          isPaid: false,
          riskFlags: [],
          unavailableSections: ['planning', 'flood', 'epc', 'title', 'taxFlags'],
          heritageDesignations: {
            listedBuilding: false,
            scheduledMonument: false,
            registeredParkOrGarden: false,
            registeredBattlefield: false,
            protectedWreckSite: false,
          },
          conservationArea: { inConservationArea: false },
          planningConstraints: {
            greenBelt: null,
            aonb: null,
            nationalPark: null,
            floodZone2or3: null,
            tpo: null,
            sssi: null,
          },
          article4Direction: { applies: false },
          planningApplications: [],
        });
      } else {
        setReport(data.report);
        setPreviewData(null);
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('There is a problem loading this report');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!report?.isPaid) {
      onPaymentRequired();
      return;
    }
    setDownloading(true);
    setError('');
    try {
      const data = await api.downloadPlanningReportPDF(reportId);
      window.open(data.downloadUrl, '_blank');
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.code === 'PAYMENT_REQUIRED') {
          onPaymentRequired();
        } else {
          setError(err.message);
        }
      } else {
        setError('There is a problem downloading the PDF');
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" aria-label="Loading report">
        <Card>
          <div className="space-y-4">
            <Skeleton className="h-8 w-2/5" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </Card>
        <Card>
          <div className="space-y-3">
            <Skeleton className="h-5 w-1/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-l-4 border-red-700" role="alert">
        <h2 className="text-lg font-semibold text-red-800 mb-2">There is a problem</h2>
        <p className="text-red-800">{error}</p>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card>
        <div className="text-center py-4">
          <p className="text-slate-700 font-medium mb-2">Report not found</p>
          <p className="text-sm text-slate-700 mb-4">
            This report may have expired or the link may be incorrect.
          </p>
          <a href="/" className="text-sm font-semibold text-emerald-700 hover:text-emerald-800">
            ← Back to home
          </a>
        </div>
      </Card>
    );
  }

  const isPreviewMode = !report.isPaid;

  return (
    <div className="space-y-6">
      <SectionHeadline
        report={report}
        isPreviewMode={isPreviewMode}
        previewData={previewData}
        onPaymentRequired={onPaymentRequired}
        isPaymentPending={isPaymentPending}
        onDownloadPDF={handleDownloadPDF}
        downloading={downloading}
      />

      <SectionCoverage report={report} />

      {!isPreviewMode && <SectionEpc report={report} />}
      {!isPreviewMode && <SectionFlood report={report} />}
      {!isPreviewMode && <SectionHeritage report={report} />}
      {!isPreviewMode && <SectionPlanning report={report} />}
      {!isPreviewMode && <SectionHistory report={report} />}
      {!isPreviewMode && <SectionTax report={report} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — Headline
// ---------------------------------------------------------------------------

interface HeadlineProps {
  report: PlanningReport;
  isPreviewMode: boolean;
  previewData: PreviewData | null;
  onPaymentRequired: () => void;
  isPaymentPending: boolean;
  onDownloadPDF: () => void;
  downloading: boolean;
}

function bucketFlags(flags: RiskFlag[]) {
  return {
    high: flags.filter((f) => f.severity === 'high'),
    warning: flags.filter((f) => f.severity === 'warning'),
    info: flags.filter((f) => f.severity === 'info'),
  };
}

function SectionHeadline({
  report,
  isPreviewMode,
  previewData,
  onPaymentRequired,
  isPaymentPending,
  onDownloadPDF,
  downloading,
}: HeadlineProps) {
  return (
    <Card>
      <h1 className="text-3xl font-bold text-slate-950 mb-1">Buyer&apos;s Report</h1>
      <div className="text-xl font-semibold text-slate-950 mt-4 leading-tight">
        <div>{report.address.line1}</div>
        {report.address.line2 && <div>{report.address.line2}</div>}
        <div>{report.address.city}</div>
        <div>{report.address.postcode}</div>
      </div>

      <dl className="mt-4 space-y-1 text-sm text-slate-700">
        <div>Generated {formatGovDateTime(report.generatedAt)}</div>
        <div>
          Report ID: <span className="font-mono text-xs">{report.reportId}</span>
        </div>
        {report.address.uprn && (
          <div>
            UPRN: <span className="font-mono text-xs">{report.address.uprn}</span>
          </div>
        )}
        {report.localAuthority && <div>Local planning authority: {report.localAuthority.name}</div>}
        {report.paidAt && <div>Paid on {formatGovDate(report.paidAt)}</div>}
      </dl>

      <hr className="my-6 border-slate-200" />

      <h2 className="text-lg font-semibold text-slate-950 mb-3">Findings at a glance</h2>

      {isPreviewMode && previewData ? (
        <PreviewBanner
          previewData={previewData}
          onPaymentRequired={onPaymentRequired}
          isPaymentPending={isPaymentPending}
        />
      ) : (
        <PaidFindings report={report} onDownloadPDF={onDownloadPDF} downloading={downloading} />
      )}
    </Card>
  );
}

function PreviewBanner({
  previewData,
  onPaymentRequired,
  isPaymentPending,
}: {
  previewData: PreviewData;
  onPaymentRequired: () => void;
  isPaymentPending: boolean;
}) {
  return (
    <div className="space-y-4">
      <p className="text-slate-800">{previewData.summary}</p>
      {previewData.keyFindings.length > 0 && (
        <ul className="list-disc list-inside text-sm text-slate-800 space-y-1">
          {previewData.keyFindings.map((finding, i) => (
            <li key={i}>{finding}</li>
          ))}
        </ul>
      )}
      <p className="text-sm text-slate-700">
        Purchase the full report for the eight sections covering EPC, flood risk, heritage,
        planning, history, tax and a downloadable PDF.
      </p>
      <Button onClick={onPaymentRequired} disabled={isPaymentPending} aria-busy={isPaymentPending}>
        {isPaymentPending ? 'Redirecting to Stripe...' : 'Pay £29'}
      </Button>
      <p className="text-xs text-slate-700">
        Payment is handled by Stripe. You&apos;ll be redirected to Stripe&apos;s secure payment
        page.
      </p>
    </div>
  );
}

function PaidFindings({
  report,
  onDownloadPDF,
  downloading,
}: {
  report: PlanningReport;
  onDownloadPDF: () => void;
  downloading: boolean;
}) {
  const flags = report.riskFlags ?? [];
  const buckets = bucketFlags(flags);

  return (
    <div className="space-y-4">
      {flags.length === 0 ? (
        <p className="text-slate-800">{HEADLINE.introWhenAllClear}</p>
      ) : (
        <>
          <p className="text-sm text-slate-700">{HEADLINE.introWhenFlagsRaised}</p>
          {buckets.high.length > 0 && (
            <Callout severity="high" heading={`${HEADLINE.bucketHigh} (${buckets.high.length})`}>
              <ul className="list-disc list-inside space-y-1">
                {buckets.high.map((f) => (
                  <li key={f.code}>{f.message}</li>
                ))}
              </ul>
            </Callout>
          )}
          {buckets.warning.length > 0 && (
            <Callout
              severity="warning"
              heading={`${HEADLINE.bucketWarning} (${buckets.warning.length})`}
            >
              <ul className="list-disc list-inside space-y-1">
                {buckets.warning.map((f) => (
                  <li key={f.code}>{f.message}</li>
                ))}
              </ul>
            </Callout>
          )}
          {buckets.info.length > 0 && (
            <Callout severity="info" heading={`${HEADLINE.bucketInfo} (${buckets.info.length})`}>
              {buckets.info.length <= 3 ? (
                <ul className="list-disc list-inside space-y-1">
                  {buckets.info.map((f) => (
                    <li key={f.code}>{f.message}</li>
                  ))}
                </ul>
              ) : (
                <p>
                  {buckets.info.length} items recorded for information. See the relevant sections
                  below for the full text.
                </p>
              )}
            </Callout>
          )}
        </>
      )}

      <p className="text-sm text-slate-700">{HEADLINE.closing}</p>

      <div className="pt-2">
        <Button
          variant="secondary"
          onClick={onDownloadPDF}
          disabled={downloading}
          loading={downloading}
          loadingText="Downloading..."
        >
          Download PDF
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — Coverage
// ---------------------------------------------------------------------------

function SectionCoverage({ report }: { report: PlanningReport }) {
  const unavailable = new Set(report.unavailableSections ?? []);
  const rows: Array<{ topic: string; source: string; status: string }> = [
    {
      topic: 'Heritage and conservation',
      source: 'Historic England (NHLE)',
      status: unavailable.has('planning') ? 'Temporarily unavailable' : 'Checked',
    },
    {
      topic: 'Planning constraints',
      source: 'planning.data.gov.uk',
      status: unavailable.has('planning') ? 'Temporarily unavailable' : 'Checked',
    },
    {
      topic: 'Planning application history',
      source: 'planning.data.gov.uk',
      status: unavailable.has('planning')
        ? 'Temporarily unavailable'
        : 'Checked — coverage incomplete',
    },
    {
      topic: 'Energy Performance Certificate (EPC)',
      source: 'EPB Register',
      status: unavailable.has('epc') ? 'Temporarily unavailable' : 'Checked',
    },
    {
      topic: 'Flood risk',
      source: 'Environment Agency',
      status: unavailable.has('flood') ? 'Temporarily unavailable' : 'Checked',
    },
    {
      topic: 'Tax and regulatory flags',
      source: 'UK statutory rules',
      status: unavailable.has('taxFlags') ? 'Temporarily unavailable' : 'Checked',
    },
    {
      topic: 'Title summary',
      source: 'HM Land Registry',
      status: 'Not yet checked — planned',
    },
  ];

  return (
    <Card>
      <SectionHeading number={2} question={COVERAGE.question} />
      <p className="text-slate-800 mb-6">{COVERAGE.intro}</p>

      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Topic</th>
              <th className="text-left px-4 py-2 font-semibold">Source</th>
              <th className="text-right px-4 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.topic}
                className={i % 2 === 1 ? 'bg-slate-50 border-t border-slate-200' : 'border-t border-slate-200'}
              >
                <td className="px-4 py-2 text-slate-900">{row.topic}</td>
                <td className="px-4 py-2 text-slate-700">{row.source}</td>
                <td className="px-4 py-2 text-slate-700 text-right">{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-sm text-slate-700 mt-4">{COVERAGE.closing}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — EPC
// ---------------------------------------------------------------------------

function SectionEpc({ report }: { report: PlanningReport }) {
  return (
    <Card>
      <SectionHeading number={3} question={EPC.question} />
      <p className="text-slate-800 mb-6">{EPC.intro}</p>

      {!report.epcData ? (
        <>
          <p className="text-slate-700">{EPC.noEpc}</p>
          <SourceFooter text={EPC.source} />
        </>
      ) : (
        <EpcContent report={report} />
      )}
    </Card>
  );
}

function EpcContent({ report }: { report: PlanningReport }) {
  const epc = report.epcData!;
  const certDate = new Date(epc.certificateDate);
  const tenYearsAgo = new Date();
  tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
  const isExpired = !Number.isNaN(certDate.getTime()) && certDate < tenYearsAgo;
  const recommendations = report.epc?.recommendations ?? [];

  return (
    <>
      <div className="flex items-end gap-8 mb-6">
        <RatingBadge rating={epc.currentRating} label={`Current: ${epc.currentRating} (${epc.currentScore}/100)`} />
        <RatingBadge
          rating={epc.potentialRating as EpcRating}
          label={`Potential: ${epc.potentialRating} (${epc.potentialScore}/100)`}
        />
      </div>

      {isExpired && (
        <Callout
          severity="warning"
          heading={EPC.certificateExpiredHeading}
          className="mb-4"
        >
          <p>{EPC.certificateExpiredBody}</p>
        </Callout>
      )}

      {(epc.currentRating === 'F' || epc.currentRating === 'G') && (
        <Callout severity="warning" heading={EPC.belowMinimumHeading} className="mb-4">
          <p>{EPC.belowMinimumBody}</p>
        </Callout>
      )}

      <h3 className="text-base font-semibold text-slate-950 mb-2">Certificate detail</h3>
      <dl className="text-sm text-slate-800 mb-6 space-y-1">
        <DLRow label="Certificate date" value={`${formatGovDate(epc.certificateDate)}${isExpired ? ' (expired)' : ''}`} />
        {epc.propertyType && <DLRow label="Property type" value={epc.propertyType} />}
        {epc.totalFloorArea && <DLRow label="Total floor area" value={`${epc.totalFloorArea} m²`} />}
      </dl>

      {recommendations.length > 0 && (
        <div className="mb-2">
          <h3 className="text-base font-semibold text-slate-950 mb-2">{EPC.improvementsHeading}</h3>
          <p className="text-sm text-slate-700 mb-3">{EPC.improvementsIntro}</p>
          <ul className="space-y-3">
            {recommendations.map((rec, i) => {
              const meta: string[] = [];
              if (rec.indicativeCost) meta.push(`Cost: ${rec.indicativeCost}`);
              if (rec.typicalSaving) meta.push(`Saving: ${rec.typicalSaving}`);
              if (typeof rec.impactOnRating === 'number') {
                meta.push(`Improved rating: ${rec.impactOnRating}/100`);
              }
              return (
                <li
                  key={`${rec.code ?? 'rec'}-${i}`}
                  className="border border-slate-200 rounded-sm p-3 bg-slate-50"
                >
                  <p className="text-sm font-semibold text-slate-950">{rec.text}</p>
                  {meta.length > 0 && (
                    <p className="text-xs text-slate-700 mt-1">{meta.join(' · ')}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <SourceFooter text={EPC.source} />
    </>
  );
}

function RatingBadge({ rating, label }: { rating: EpcRating; label: string }) {
  return (
    <div className="flex flex-col items-start">
      <div
        className={`w-16 h-16 rounded-md flex items-center justify-center text-2xl font-bold text-white ${RATING_BG[rating] ?? RATING_BG.unknown}`}
      >
        {rating === 'unknown' ? '?' : rating}
      </div>
      <p className="text-sm text-slate-700 mt-2">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 4 — Flood risk
// ---------------------------------------------------------------------------

function SectionFlood({ report }: { report: PlanningReport }) {
  if (!report.flood) {
    return (
      <Card>
        <SectionHeading number={4} question={FLOOD.question} />
        <p className="text-slate-700">
          Flood risk data is temporarily unavailable for this property. The Environment Agency feed
          did not return a result during this report run.
        </p>
        <SourceFooter text={FLOOD.source} />
      </Card>
    );
  }

  const flood = report.flood;
  const zone = flood.riversAndSeaZone as FloodRiversAndSeaZone;
  const surface = flood.surfaceWaterRisk as SurfaceWaterRisk;
  const zoneIsRisky = zone === '2' || zone === '3a' || zone === '3b';
  const surfaceIsHigh = surface === 'high';
  const showCallout = zoneIsRisky || surfaceIsHigh;
  const calloutSeverity =
    surfaceIsHigh || zone === '3a' || zone === '3b' ? 'high' : 'warning';

  return (
    <Card>
      <SectionHeading number={4} question={FLOOD.question} />
      <p className="text-slate-800 mb-6">{FLOOD.intro}</p>

      <h3 className="text-base font-semibold text-slate-950 mb-2">{FLOOD.riversHeading}</h3>
      <p className="text-sm text-slate-900 mb-1">
        <span className="font-semibold">Recorded zone:</span> {FLOOD.riversZoneLabel[zone] ?? FLOOD.riversZoneLabel.unknown}
      </p>
      <p className="text-sm text-slate-700 mb-6">
        {FLOOD.riversZoneExplanation[zone] ?? FLOOD.riversZoneExplanation.unknown}
      </p>

      <h3 className="text-base font-semibold text-slate-950 mb-2">{FLOOD.surfaceHeading}</h3>
      <p className="text-sm text-slate-900 mb-1">
        <span className="font-semibold">Recorded risk:</span> {FLOOD.surfaceLabel[surface] ?? FLOOD.surfaceLabel.unknown}
      </p>
      <p className="text-sm text-slate-700 mb-6">
        {FLOOD.surfaceExplanation[surface] ?? FLOOD.surfaceExplanation.unknown}
      </p>

      <h3 className="text-base font-semibold text-slate-950 mb-2">{FLOOD.historicalHeading}</h3>
      {flood.historicalEvents.length === 0 ? (
        <p className="text-sm text-slate-700 mb-4">{FLOOD.noHistoricalEvents}</p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-sm mb-4">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Date</th>
                <th className="text-left px-4 py-2 font-semibold">Severity</th>
                <th className="text-left px-4 py-2 font-semibold">Source</th>
              </tr>
            </thead>
            <tbody>
              {flood.historicalEvents.map((e, i) => (
                <tr
                  key={i}
                  className={i % 2 === 1 ? 'bg-slate-50 border-t border-slate-200' : 'border-t border-slate-200'}
                >
                  <td className="px-4 py-2 text-slate-900">{formatGovDate(e.date)}</td>
                  <td className="px-4 py-2 text-slate-800">
                    {e.severity ? e.severity.charAt(0).toUpperCase() + e.severity.slice(1) : '—'}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{e.source ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCallout && (
        <Callout
          severity={calloutSeverity}
          heading={FLOOD.highRiskCalloutHeading}
          className="mb-2"
        >
          <p>{FLOOD.highRiskCalloutBody}</p>
        </Callout>
      )}

      <SourceFooter text={FLOOD.source} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section 5 — Heritage and conservation
// ---------------------------------------------------------------------------

function SectionHeritage({ report }: { report: PlanningReport }) {
  const heritage = report.heritageDesignations;
  const conservation = report.conservationArea;

  const rows: Array<{ label: string; yes: boolean }> = [
    {
      label:
        'Listed building' +
        (heritage.listedBuilding && heritage.listedBuildingGrade
          ? ` (Grade ${heritage.listedBuildingGrade})`
          : ''),
      yes: heritage.listedBuilding,
    },
    { label: 'Scheduled monument', yes: heritage.scheduledMonument },
    { label: 'Registered park or garden', yes: heritage.registeredParkOrGarden },
    { label: 'Registered battlefield', yes: heritage.registeredBattlefield },
    { label: 'Protected wreck site', yes: heritage.protectedWreckSite },
  ];

  return (
    <Card>
      <SectionHeading number={5} question={HERITAGE.question} />
      <p className="text-slate-800 mb-6">{HERITAGE.intro}</p>

      <h3 className="text-base font-semibold text-slate-950 mb-2">{HERITAGE.designationsHeading}</h3>
      <DesignationTable rows={rows} />

      {heritage.listedBuilding && (
        <Callout
          severity="warning"
          heading={HERITAGE.listedCalloutHeading(heritage.listedBuildingGrade)}
          className="mt-4"
        >
          <p>{HERITAGE.listedCalloutBody(heritage.listedBuildingGrade)}</p>
        </Callout>
      )}

      <h3 className="text-base font-semibold text-slate-950 mt-6 mb-2">{HERITAGE.conservationHeading}</h3>
      <p className="text-sm text-slate-900 mb-2">
        <span className="font-semibold">Within a conservation area:</span>{' '}
        {conservation.inConservationArea ? 'Yes' : 'No'}
      </p>
      {conservation.inConservationArea ? (
        <>
          <p className="text-sm text-slate-800 mb-4">
            {HERITAGE.inConservation(conservation.conservationAreaName)}
          </p>
          <Callout severity="info" heading={HERITAGE.conservationCalloutHeading}>
            <p>{HERITAGE.conservationCalloutBody}</p>
          </Callout>
        </>
      ) : (
        <p className="text-sm text-slate-700">{HERITAGE.noConservation}</p>
      )}

      <SourceFooter text={HERITAGE.source} />
    </Card>
  );
}

function DesignationTable({ rows }: { rows: Array<{ label: string; yes: boolean }> }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-sm">
      <table className="w-full text-sm">
        <thead className="bg-slate-100">
          <tr>
            <th className="text-left px-4 py-2 font-semibold">Designation</th>
            <th className="text-right px-4 py-2 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.label}
              className={i % 2 === 1 ? 'bg-slate-50 border-t border-slate-200' : 'border-t border-slate-200'}
            >
              <td className="px-4 py-2 text-slate-900">{row.label}</td>
              <td
                className={`px-4 py-2 text-right ${row.yes ? 'font-semibold text-slate-950' : 'text-slate-700'}`}
              >
                {row.yes ? 'Yes' : 'No'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 6 — Planning constraints and rights
// ---------------------------------------------------------------------------

function SectionPlanning({ report }: { report: PlanningReport }) {
  const c = report.planningConstraints;
  const article4 = report.article4Direction;

  const rows: Array<{ key: keyof typeof PLANNING.whatThisMeans; label: string; value: boolean | null }> = [
    { key: 'green_belt', label: 'Green Belt', value: c.greenBelt },
    { key: 'aonb', label: 'Area of Outstanding Natural Beauty (AONB)', value: c.aonb },
    { key: 'national_park', label: 'National Park', value: c.nationalPark },
    { key: 'tpo', label: 'Tree Preservation Order (TPO)', value: c.tpo },
    { key: 'sssi', label: 'Site of Special Scientific Interest (SSSI)', value: c.sssi },
    { key: 'article4', label: 'Article 4 Direction', value: article4.applies },
  ];

  return (
    <Card>
      <SectionHeading number={6} question={PLANNING.question} />
      <p className="text-slate-800 mb-6">{PLANNING.intro}</p>

      <h3 className="text-base font-semibold text-slate-950 mb-2">{PLANNING.permittedDevelopmentHeading}</h3>
      <p className="text-slate-800 mb-6">
        {article4.applies ? PLANNING.permittedDevelopmentRestricted : PLANNING.permittedDevelopmentClear}
      </p>

      <div className="overflow-x-auto border border-slate-200 rounded-sm mb-4">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Constraint</th>
              <th className="text-right px-4 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const status =
                row.value === null || row.value === undefined
                  ? 'Not recorded'
                  : row.value
                  ? 'Yes'
                  : 'No';
              const isYes = row.value === true;
              return (
                <tr
                  key={row.label}
                  className={i % 2 === 1 ? 'bg-slate-50 border-t border-slate-200' : 'border-t border-slate-200'}
                >
                  <td className="px-4 py-2 text-slate-900">{row.label}</td>
                  <td
                    className={`px-4 py-2 text-right ${isYes ? 'font-semibold text-slate-950' : 'text-slate-700'}`}
                  >
                    {status}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {article4.applies && (
        <div className="mb-4">
          <h3 className="text-base font-semibold text-slate-950 mb-2">
            Article 4 Direction — recorded detail
          </h3>
          <dl className="text-sm space-y-1">
            {article4.issuingAuthority && <DLRow label="Issuing authority" value={article4.issuingAuthority} />}
            {article4.directionType && <DLRow label="Direction type" value={article4.directionType} />}
            {article4.effectiveDate && (
              <DLRow label="Effective date" value={formatGovDate(article4.effectiveDate)} />
            )}
          </dl>
        </div>
      )}

      <div className="space-y-3">
        {rows
          .filter((r) => r.value === true)
          .map((row) => {
            const copy = PLANNING.whatThisMeans[row.key];
            return (
              <Callout key={row.key} severity="warning" heading={copy.heading}>
                <p>{copy.body}</p>
              </Callout>
            );
          })}
      </div>

      <SourceFooter text={PLANNING.source} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section 7 — Planning application history
// ---------------------------------------------------------------------------

function SectionHistory({ report }: { report: PlanningReport }) {
  const apps = report.planningApplications ?? [];

  return (
    <Card>
      <SectionHeading number={7} question={HISTORY.question} />
      <p className="text-slate-800 mb-6">{HISTORY.intro}</p>

      <Callout
        severity="info"
        heading={HISTORY.coverageCalloutHeading}
        className="mb-6"
      >
        <p>{HISTORY.coverageCalloutBody}</p>
      </Callout>

      {apps.length === 0 ? (
        <p className="text-sm text-slate-700">{HISTORY.noApplications}</p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Reference</th>
                <th className="text-left px-4 py-2 font-semibold">Decision</th>
                <th className="text-left px-4 py-2 font-semibold">Decision date</th>
                <th className="text-left px-4 py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((app, i) => (
                <tr
                  key={`${app.reference}-${i}`}
                  className={i % 2 === 1 ? 'bg-slate-50 border-t border-slate-200' : 'border-t border-slate-200'}
                >
                  <td className="px-4 py-2 text-slate-900">{app.reference}</td>
                  <td className="px-4 py-2 text-slate-800">{app.decision}</td>
                  <td className="px-4 py-2 text-slate-800">
                    {app.decisionDate ? formatGovDate(app.decisionDate) : 'Pending'}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{app.description ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SourceFooter text={HISTORY.source} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section 8 — Tax and regulatory flags
// ---------------------------------------------------------------------------

function SectionTax({ report }: { report: PlanningReport }) {
  const tax = report.taxFlags;
  const standardBands = (tax?.sdltBands ?? [])
    .filter((b) => b.buyerType === 'standard')
    .sort((a, b) => a.threshold - b.threshold);

  return (
    <Card>
      <SectionHeading number={8} question={TAX.question} />
      <p className="text-slate-800 mb-6">{TAX.intro}</p>

      <h3 className="text-base font-semibold text-slate-950 mb-2">{TAX.councilTaxHeading}</h3>
      {tax?.councilTaxBand ? (
        <p className="text-slate-800 mb-6">{TAX.councilTaxBody(tax.councilTaxBand)}</p>
      ) : (
        <p className="text-sm text-slate-700 mb-6">{TAX.councilTaxNotRecorded}</p>
      )}

      <h3 className="text-base font-semibold text-slate-950 mb-2">{TAX.sdltHeading}</h3>
      <p className="text-slate-800 mb-4">{TAX.sdltIntro}</p>
      {standardBands.length === 0 ? (
        <p className="text-sm text-slate-700 mb-6">
          SDLT band data is temporarily unavailable. Confirm current bands at gov.uk/stamp-duty-land-tax.
        </p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-sm mb-6">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Portion of price</th>
                <th className="text-right px-4 py-2 font-semibold">Rate</th>
              </tr>
            </thead>
            <tbody>
              {standardBands.map((band, i) => {
                const next = standardBands[i + 1];
                const lower = `£${(band.threshold / 100).toLocaleString('en-GB')}`;
                const upper = next ? `£${(next.threshold / 100).toLocaleString('en-GB')}` : null;
                return (
                  <tr
                    key={band.threshold}
                    className={i % 2 === 1 ? 'bg-slate-50 border-t border-slate-200' : 'border-t border-slate-200'}
                  >
                    <td className="px-4 py-2 text-slate-900">
                      {upper ? `${lower} – ${upper}` : `Above ${lower}`}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-800">{band.rate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tax?.section24Applicable && (
        <>
          <h3 className="text-base font-semibold text-slate-950 mb-2">{TAX.section24Heading}</h3>
          <p className="text-slate-800 mb-6">{TAX.section24Body}</p>
        </>
      )}

      <h3 className="text-base font-semibold text-slate-950 mb-2">{TAX.leaseholdHeading}</h3>
      {tax?.leaseholdReformApplicable ? (
        <>
          <p className="text-slate-800 mb-4">{TAX.leaseholdBody}</p>
          <Callout severity="info" heading="How this might affect you">
            <p>
              If you buy a leasehold property, the 2024 Act gives you extended rights to challenge
              ground rent, extend the lease, or buy the freehold on more favourable terms. Some
              sections are in force from 2024; others are scheduled to come in by 2026 through
              secondary legislation.
            </p>
          </Callout>
        </>
      ) : report.unavailableSections.includes('title') ? (
        <p className="text-sm text-slate-700">{TAX.leaseholdTenureUnknown}</p>
      ) : (
        <p className="text-sm text-slate-700">{TAX.leaseholdNotApplicable}</p>
      )}

      <SourceFooter text={TAX.source} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

function SectionHeading({ number, question }: { number: number; question: string }) {
  return (
    <h2 className="text-2xl font-bold text-slate-950 mb-4 pb-2 border-b border-slate-200">
      <span className="text-slate-500 mr-2">{number}.</span>
      {question}
    </h2>
  );
}

function DLRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex">
      <dt className="font-semibold text-slate-900 mr-2">{label}:</dt>
      <dd className="text-slate-800">{value}</dd>
    </div>
  );
}

function SourceFooter({ text }: { text: string }) {
  return (
    <p className="text-xs text-slate-700 pt-4 mt-6 border-t border-slate-200">
      <span className="font-semibold">Source:</span> {text}
    </p>
  );
}
